'use client';

/**
 * PropertyMapCard Component
 *
 * Compact horizontal card layout optimized for map view display
 * Core reusable component - theme-agnostic
 *
 * Now uses FormatPrice for currency-aware price display with automatic conversion.
 */

import { FormatPrice } from '@/components/ui/FormatPrice';
import { extractBestPriceSimple } from '@/lib/utils/price';
import type { Property } from '@/lib/api/clients/properties';
import Image from 'next/image';
import Link from 'next/link';

export interface PropertyMapCardProps {
  /**
   * Property data
   */
  property: Property;
  /**
   * Optional labels for translations
   */
  labels?: {
    bedrooms?: string;
    bathrooms?: string;
    area?: string;
    viewDetails?: string;
  };
  /**
   * Optional location name to display
   */
  locationName?: string;
  /**
   * Link href
   */
  href?: string;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Whether this card is currently selected
   */
  isSelected?: boolean;
  /**
   * Whether to show original price alongside converted price
   */
  showOriginalPrice?: boolean;
  /**
   * Icon components (optional - themes can provide custom icons)
   */
  icons?: {
    bedroom?: GenericIconComponent;
    bathroom?: GenericIconComponent;
    area?: GenericIconComponent;
  };
  /**
   * Icon colors (optional - themes can provide custom colors)
   */
  iconColors?: {
    bedroom?: string;
    bathroom?: string;
    area?: string;
  };
  /**
   * Custom styles for text elements (optional - themes can provide custom styles)
   */
  textStyles?: {
    location?: string;
    title?: string;
    price?: string;
    details?: string;
  };
  /**
   * Custom inline styles (optional - themes can provide custom styles)
   */
  style?: React.CSSProperties;
}

// Generic icon props - compatible with various icon libraries
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GenericIconComponent = React.ComponentType<any>;

/**
 * PropertyMapCard - Compact horizontal layout for map views
 */
export function PropertyMapCard({
  property,
  labels = {
    bedrooms: 'Beds',
    bathrooms: 'Baths',
    area: 'Area',
    viewDetails: 'View',
  },
  locationName,
  href,
  className = '',
  isSelected = false,
  showOriginalPrice = false,
  icons,
  iconColors,
  textStyles,
  style,
}: PropertyMapCardProps) {
  const { amount: askingPrice, currency } = extractBestPriceSimple(property);
  const hasPrice = askingPrice != null && askingPrice > 0;

  const area = property.totalFloorArea
    ? `${property.totalFloorArea.toLocaleString()} ${(property.totalFloorAreaUnit || 'sqft').toLowerCase()}`
    : null;

  // Icon components with defaults
  const BedroomIcon = icons?.bedroom;
  const BathroomIcon = icons?.bathroom;
  const AreaIcon = icons?.area;

  const cardContent = (
    <div
      className={`
        flex flex-row gap-4 bg-white rounded-lg overflow-hidden transition-all duration-200
        ${isSelected ? 'ring-2 ring-primary shadow-lg' : 'hover:shadow-md'}
        ${className}
      `}
      style={style}
    >
      {/* Image - Smaller for map view */}
      <div className="relative w-32 h-32 shrink-0 bg-gray-100">
        {property.heroMedia?.url || property.primaryImageUrl ? (
          <Image
            src={property.heroMedia?.url || property.primaryImageUrl || ''}
            alt={property.name}
            width={128}
            height={128}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gray-200 text-xs">
            No Image
          </div>
        )}
      </div>

      {/* Content - Compact */}
      <div className="flex-1 py-3 pr-3 min-w-0" style={style}>
        {/* Location (if provided) */}
        {locationName && (
          <p
            className={
              textStyles?.location || 'text-xs text-gray-500 mb-1 truncate'
            }
            style={style}
          >
            {locationName}
          </p>
        )}

        {/* Property Name - Single line */}
        <h2
          className={
            textStyles?.title ||
            'text-base font-bold text-gray-900 mb-1 line-clamp-1'
          }
          style={style}
        >
          {property.name}
        </h2>

        {/* Price - Now uses FormatPrice with currency conversion */}
        {hasPrice ? (
          <div
            className={textStyles?.price || 'text-lg font-bold text-gray-900 mb-2'}
            style={style}
          >
            <FormatPrice
              amount={askingPrice}
              currency={currency}
              size="md"
              showOriginal={showOriginalPrice}
            />
          </div>
        ) : (
          <p className="text-sm font-medium text-gray-500 italic mb-2" style={style}>
            Contact for price
          </p>
        )}

        {/* Property Details - Compact */}
        <div
          className={
            textStyles?.details || 'flex items-center gap-3 text-sm text-gray-600'
          }
          style={style}
        >
          {property.bedrooms !== null && property.bedrooms !== undefined && (
            <div className="flex items-center gap-1">
              {BedroomIcon && (
                <BedroomIcon
                  size={16}
                  weight="regular"
                  style={{ color: iconColors?.bedroom }}
                />
              )}
              <span style={style}>
                {property.bedrooms} {labels.bedrooms}
              </span>
            </div>
          )}

          {property.bathrooms !== null && property.bathrooms !== undefined && (
            <div className="flex items-center gap-1">
              {BathroomIcon && (
                <BathroomIcon
                  size={16}
                  weight="regular"
                  style={{ color: iconColors?.bathroom }}
                />
              )}
              <span style={style}>
                {property.bathrooms} {labels.bathrooms}
              </span>
            </div>
          )}

          {area && (
            <div className="flex items-center gap-1">
              {AreaIcon && (
                <AreaIcon
                  size={16}
                  weight="regular"
                  style={{ color: iconColors?.area }}
                />
              )}
              <span className="truncate" style={style}>
                {area}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Wrap in Link if href is provided
  if (href) {
    return (
      <Link href={href} className="block">
        {cardContent}
      </Link>
    );
  }

  return cardContent;
}
